import {
    addMinutes,
    differenceInMinutes,
    endOfDay,
    format,
    getISODay,
    isAfter,
    isBefore,
    isSameDay,
    isSaturday,
    isSunday,
    isWeekend,
    parse,
    startOfDay
} from "date-fns";
import Fuse from "fuse.js";
import {cloneDeep, each, find, findIndex, kebabCase, map, sortBy} from "lodash";
import { createNanoEvents } from "nanoevents";
import {computed, inject, reactive, ref, watch} from "vue";
import { betweenDates, getNow, getToday, parseFromISO } from "../utils/date.js";
import {slugify, sortAndPrepareRouteGroups} from "../utils/helpers";
import {usePage} from '@inertiajs/vue3';
import {defineStore} from "pinia";
import {formatTime} from "../utils/date.js";
import {LEG_MODE_BUS} from "../utils/icons";

const emitter = createNanoEvents();
let api = null;
let unwatchHandlers = [];
const now = getNow();
const page = usePage();

export const useRouteStore = defineStore('routes', () => {
    const isLoading = ref(false)
    const isProcessingSchedule = ref(false)
    const dataFetched = ref(false)

    const routes = reactive({
        data: [],
        groups: [],
        schedules: {},
        current: null,
        selected: [],
        fetched: false,
        scroll: {},
    })

    const routeGroups = ref([])
    const selectedSchedule = ref(null)
    const selectedStops = ref([])

    const alerts = reactive({
        data: [],
        stops: {},
        routes: {},
        groups: {},
        fetched: false,
    })

    const filters = reactive({
        directionOfTravel: {
            options: [],
            selected: null,
            selectedLabel: null,
            headsignLabel: null,
        },

        timeOfTravel: {
            selected: '0',
        },

        dayOfTravel: {
            options: [],
            selected: format(now, 'MMM d, yyyy'),
            selectedLabel: null,
            selectedDate: now,
            selectedDayIndex: getISODay(now) - 1,
            isToday: true,
            isWeekend: isWeekend(now),
            isSaturday: isSaturday(now),
            isSunday: isSunday(now),
            isWeekday: !isWeekend(now),
        },
    })

    const routeSearch = ref(null)

    function getRunsOn() {
        const dayOfTravel = filters.dayOfTravel;

        // When a not specific date is selected, we try to use
        // the selected label (weekdays, saturdays...)
        if (! dayOfTravel.selectedDate) {
            if (! dayOfTravel.selectedLabel) {
                return '';
            }

            return `on <strong>${dayOfTravel.selectedLabel}</strong>`;
        }

        // A specific dated has been selected.
        if (dayOfTravel.isWeekday) {
            return  'on <strong>Weekdays</strong>';
        }
        if (dayOfTravel.isSaturday) {
            return 'on <strong>Saturdays</strong>';
        }
        if (dayOfTravel.isSunday) {
            return 'on <strong>Sundays</strong>';
        }
        if (dayOfTravel.isWeekend) {
            return 'on <strong>Weekends</strong>';
        }

        return '';
    }

    function setDirectionOfTravelValue(value) {
        filters.directionOfTravel.selected = value;
        const label = filters.directionOfTravel.options.find((opt) => opt.value === value)?.label;

        filters.directionOfTravel.selectedLabel = label ? label : 'null';
        filters.directionOfTravel.headsignLabel = label ? label.replace('bound', '') : null;

        setTimeOfTravelValue(-1);
    }

    function setDirectionOfTravelOptions(options) {
        filters.directionOfTravel.options = options;
    }

    function setDayOfTravelValue(value) {
        filters.dayOfTravel.selected = value.value;
        filters.dayOfTravel.selectedLabel = value.label;
        filters.dayOfTravel.selectedDate = null;
        filters.dayOfTravel.selectedDayIndex = null;

        const today = getToday();

        const dateIsWeekday = filters.dayOfTravel.selectedLabel === 'Weekdays' && !isWeekend(today);
        // saturdays option is selected and date is a saturday
        const dateIsSaturday = filters.dayOfTravel.selectedLabel === 'Saturdays' && isSaturday(today);
        // sundays option is selected and date is a sunday
        const dateIsSunday = filters.dayOfTravel.selectedLabel === 'Sundays' && isSunday(today);
        // weekends option is selected and date is a weekend day
        const dateIsWeekend = filters.dayOfTravel.selectedLabel === 'Weekends' && isWeekend(today);

        const wasTodaySelected = filters.dayOfTravel.isToday;

        filters.dayOfTravel.isToday = (dateIsWeekday || dateIsSaturday || dateIsSunday || dateIsWeekend);
        filters.dayOfTravel.isWeekday = dateIsWeekday;
        filters.dayOfTravel.isSaturday = dateIsSaturday;
        filters.dayOfTravel.isSunday = dateIsSunday;
        filters.dayOfTravel.isWeekend = dateIsWeekend;

        if (wasTodaySelected !== filters.dayOfTravel.isToday) {
            setTimeOfTravelValue(-1);
        }

        buildSelectedSchedule({
            updateRouteShape: true,
            processNextArrival: false,
        });
    }

    function setDayOfTravelDate(date) {
        filters.dayOfTravel.selectedLabel = null;
        filters.dayOfTravel.selected = format(date, 'MMM d, yyyy');
        filters.dayOfTravel.selectedDate = date;
        filters.dayOfTravel.selectedDayIndex = getISODay(date) - 1;

        const wasTodaySelected = filters.dayOfTravel.isToday;

        filters.dayOfTravel.isToday = isSameDay(date, getToday());
        filters.dayOfTravel.isWeekday = !isWeekend(date);
        filters.dayOfTravel.isSaturday = isSaturday(date);
        filters.dayOfTravel.isSunday = isSunday(date);
        filters.dayOfTravel.isWeekend = isWeekend(date);

        if (wasTodaySelected !== filters.dayOfTravel.isToday) {
            setTimeOfTravelValue(-1);
        }

        buildSelectedSchedule({
            updateRouteShape: true,
            processNextArrival: false,
        });
    }

    function setDayOfTravelOptionsFromRoute(route) {
        const daysOfTravel = [];

        Object.keys(route.schedule.summary.effective_from_dates).forEach(function (key) {
            Object.values(route.schedule.summary.effective_from_dates[key]).forEach(function (dates) {
                if (daysOfTravel.indexOf(key) >= 0) {
                    return;
                }

                daysOfTravel.push(key);
            });
        });

        const orderedDays = ["Weekdays", "Saturdays", "Sundays"];
        const intersection = orderedDays.filter(x => daysOfTravel.includes(x));
        const difference = daysOfTravel.filter(x => !orderedDays.includes(x));

        filters.dayOfTravel.options = [...intersection, ...difference].map((d) => ({
            value: d,
            label: d,
        }));

        setDayOfTravelDate(getToday());
    }

    function setTimeOfTravelValue(value) {
        if (value === null || value === undefined || value.length === 0) {
            return;
        }
        filters.timeOfTravel.selected = value.toString();
    }

    function getNextArrivalFromStops(stops) {
        const today = getNow();

        // Select the earliest schedule time when the selected date is different from today
        // and weekday filter is not selected.
        if (isWeekday(today) && ! weekdayFilterIsSelected() && selectedDateIsDifferentFromToday()){
            return { value: '0' };
        }

        if (isSaturday(today) && ! saturdayFilterIsSelected() && selectedDateIsDifferentFromToday()){
            return { value: '0' };
        }

        if (isSunday(today) && ! sundayFilterIsSelected() && selectedDateIsDifferentFromToday()){
            return { value: '0' };
        }

        // Check all stops to see what has the next arrival time
        for (let stop of stops) {
            if (selectedStops.value.length > 0 && selectedStops.value[0].code === stop.code) {
                const nextTime = stop.times.find((time) => {
                    return differenceInMinutes(time.date, today) > 0;
                });

                if (nextTime) {
                    return nextTime;
                }
            }

            const selectedTime = stop.times.find((time) => {
                return time.enabled && differenceInMinutes(time.date, today) >= 0;
            });

            if (selectedTime) {
                return selectedTime;
            }
        }

        return { value: '0' };
    }

    function isWeekday(date) {
        return ! isWeekend(date);
    }

    function weekdayFilterIsSelected() {
        return filters.dayOfTravel.selectedLabel === 'Weekdays';
    }

    function saturdayFilterIsSelected() {
        return filters.dayOfTravel.selectedLabel === 'Saturdays';
    }

    function sundayFilterIsSelected() {
        return filters.dayOfTravel.selectedLabel === 'Sundays';
    }

    function selectedDateIsDifferentFromToday() {
        const today = getToday();
        const selectedDay = filters.dayOfTravel.selectedDate;

        return (! filters.dayOfTravel?.selectedDate
            || ! isSameDay(today, selectedDay))
    }

    function setGroupScrolls(group, showTop, showBottom) {
        routeGroups.value[group].showTop = showTop === true;
        routeGroups.value[group].showBottom = showBottom === true;
    }

    function setRouteScroll(showTop, showBottom) {
        routes.scroll.showTop = showTop === true;
        routes.scroll.showBottom = showBottom === true;
    }

    /**
     * Load data from the API.
     * @return Promise
     */
    function loadDataFromApi() {
        return new Promise((resolve) => {
            isLoading.value = true;
            dataFetched.value = false;
            routes.fetched = false;
            routes.loading = true;
            alerts.fetched = false;
            alerts.loading = true;

            const apiPromises = Promise.all([
                api.getRoutes(),
                api.getServiceAlerts()
            ]);

            apiPromises.then((results) => {
                alerts.fetched = true
                alerts.data = results[1].data
                alerts.stops = results[1].refs.stops
                alerts.routes = results[1].refs.routes
                alerts.groups = results[1].refs.groups

                routes.fetched = true
                routes.schedules = {}
                routes.data = results[0].data.map((r, ri) => {
                        r.group = find(results[0].refs.groups, (g) => g.name === r.group);
                        r.alert = [];

                        alerts.data.forEach((alertElement, alertIndex) => {
                            alerts.data[alertIndex].routes.forEach((routeElement) => {
                                (routeElement.id === r.id) ? r.alert.push(alerts.data[alertIndex]) : null
                            });
                        });

                        return r;
                    })
                routes.groups = sortAndPrepareRouteGroups(results[0].refs.groups)
                routes.scroll = {'showTop': false, 'showBottom': false}

                buildRouteGroups();
                setRouteColors();

                dataFetched.value = true;
                isLoading.value = false;
                routes.fetched = true;
                routes.loading = false;
                alerts.fetched = true;
                alerts.loading = false;

                emitter.emit('routesLoaded');

                resolve();
            });
        });
    }

    function buildRouteGroups() {
        const groupedRoutes = {};

        routes.data.forEach((route) => {
            if (!groupedRoutes.hasOwnProperty(route.group.name)) {
                groupedRoutes[route.group.name] = [];
            }

            const globalGroupSettings = page.props.global.routes.groups_order.find((g) => {
                return g.custom_name === route.group.name ||
                g.group_name === route.group.name
            });

            groupedRoutes[route.group.name].push({
                ...route,
                hasAlerts: alerts.routes[route.id],
                group: {
                    ...route.group,
                    icon: globalGroupSettings?.icon_name
                }
            });
        });

        routeGroups.value = map(routes.groups, (group) => {
            const groupAlerts = alerts.groups[group.apiName] || {count: 0};

            groupedRoutes[group.apiName] = groupedRoutes[group.apiName] || [];

            const globalGroupSettings = page.props.global.routes.groups_order.find((g) => {
                return g.custom_name === group.name ||
                g.group_name === group.name
            });

            return {
                name: group.name,
                slug: kebabCase(group.name),
                mode: group.mode,
                icon: globalGroupSettings?.icon_name,
                alerts: groupAlerts,
                routes: groupedRoutes[group.apiName],
                // 4 is the number of routes that can be displayed without the need of any scrollbar.
                scrollTipVisible: groupedRoutes[group.apiName].length > 4,
                showTop: false,
                // when showing more than 4 routes, init the scroll down behavior
                showBottom: groupedRoutes[group.apiName].length > 4,
            }
        });

        routeSearch.value = new Fuse(routeGroups.value.reduce((routes, group) => routes.concat(group.routes), []), {
            threshold: 0.2,
            keys: ['longName', 'shortName'],
        });
    }

    function setRouteColors() {
        routes.data = routes.data.map((route) => {
            return {
                ...route,
                ...configureRouteColorData(route)
            }
        })
    }

    function configureRouteColorData(route){
        // First, use the color from the GTFS or set a default color
        let color = route.color ?? '502059';

        let textColor = route.textColor ?? 'FFFFFF';

        let iconName = LEG_MODE_BUS;

        // See if the custom color is defined for a route group
        const groupName = route.group.name ?? route.group;

        const groupSettings = page.props.global.routes.groups_order.find((groupOrder) => groupOrder.group_name.includes(groupName));

        if(groupSettings?.custom_route_color){
            color = groupSettings.custom_route_color.replace('#', '');
        }

        if(groupSettings?.custom_text_color){
            textColor = groupSettings.custom_text_color.replace('#', '');
        }

        if(groupSettings?.icon_name){
            iconName = groupSettings.icon_name;
        }

        // See if the color is defined as "light"
        let isLightColor = false;

        // See if a custom color was defined for the specific route
        const customRouteColors = page.props.global.custom_route_colors.find((routeColorData) => routeColorData.slug.toUpperCase() === route.id.toUpperCase());

        if(customRouteColors?.route_color){
            color = customRouteColors.route_color.replace('#', '');
        }

        if(customRouteColors?.route_text_color){
            textColor = customRouteColors.route_text_color.replace('#', '');
        }

        if(customRouteColors?.is_light_color){
            isLightColor = true;
        }

        return {
            color,
            isLightColor,
            textColor,
            iconName,
        }
    }

    /**
     * Get the index of the route.
     *
     * @param routeId
     * @returns {number}
     */
    function getRouteIndex(routeId) {
        return findIndex(routes.data, (r) => r.id.toLowerCase() === routeId.toLowerCase());
    }

    /**
     * Transform the given stop time to a date object, optionally
     * using the given base date as the year/month/day.
     *
     * @param stopTime
     * @param baseDate
     * @returns {null|Date}
     */
    function transformStopTimeToDate(stopTime, baseDate) {
        if (!stopTime) {
            return null;
        }

        const [h, m] = stopTime.split(':');
        const duration = parseInt(h) * 60 + parseInt(m);

        return addMinutes(
            startOfDay(baseDate || getToday()),
            duration
        );
    }

    /**
     * Load the schedules for the given route and cache it in the routes array.
     *
     * @param routeId
     * @returns {Promise<*>}
     */
    async function loadRouteData(routeId) {
        if (routes.fetched && routes.data.length === 0) {
            return {
                route: null
            };
        }

        const routeIndex = getRouteIndex(routeId);

        if (routeIndex < 0) {
            return {
                route: null
            };
        }

        const route = routes.data[routeIndex]

        // Load route schedule
        if (!route.schedule) {
            isLoading.value = true
            dataFetched.value = false

            route.schedule = await api.getRouteSchedules(slugify(routeId))

            isLoading.value = false
            dataFetched.value = true;
        }

        // Prepare effective dates
        const dates = (function () {
            const result = [];

            each(route.schedule.summary.effective_from_dates, (effDateDirections, effDateName) => {
                each(effDateDirections, (effDateRanges, effDateDir) => {
                    each(effDateRanges, range => {
                        result.push({
                            range,
                            direction: effDateDir,
                            calendarName: effDateName,
                        })
                    })
                });
            });

            return result;
        })();

        route.effectiveDates = dates
            .map((x) => {
                const today = getToday();

                const [fromDate, toDate] = x.range.split('-');
                const start = parse(fromDate, 'yyyy/MM/dd', today);
                const end = parse(toDate, 'yyyy/MM/dd', today);

                return {
                    calendarName: x.calendarName,
                    direction: x.direction,
                    fromDate,
                    toDate,
                    fromDateObj: start,
                    toDateObj: end,
                    formattedFromDate: format(start, 'MMM d, yyyy'),
                    formattedToDate: format(end, 'MMM d, yyyy'),
                    isPast: isAfter(today, end),
                    isFuture: isBefore(today, start),
                    isEffectiveFor(date) {
                        return (isAfter(date, start) || isSameDay(date, start)) && (isBefore(date, end) || isSameDay(date, end));
                    },
                }
            })
            .filter(effDate => !effDate.isPast)
            .sort((a, b) => a.fromDateObj - b.fromDateObj);

        // Just overwrite the cached route
        routes.data[routeIndex] = route;

        return {
            route
        };
    }

    async function setSelectedRoutes(selectedRoutes){
        routes.selected = [];
        for(const selectedRoute of selectedRoutes){
            let route = routes.data.find((r) => selectedRoute.toLowerCase() === r.id.toLowerCase())
            if(!route){
                route = (await loadRouteData(selectedRoute)).route
            }
            routes.selected = [
                ...routes.selected,
                {
                    ...route
                },
            ];
        }
    }

    async function setCurrentRoute(routeId) {
        if (!routeId) {
            return routes.current = null;
        }

        const callback = async () => {
            const { route } = await loadRouteData(routeId);

            if (!route) {
                return routes.current = null;
            }

            // Update the direction of travel filter
            const directionOfTravelOptions = (route.schedule.summary.directions || []).map((d) => ({
                value: d.id.toString(),
                label: d.headsign,
            }));

            setDirectionOfTravelOptions(directionOfTravelOptions);
            setDirectionOfTravelValue(directionOfTravelOptions.length > 0 ? directionOfTravelOptions[0].value : null);
            setDayOfTravelOptionsFromRoute(route);

            routes.current = route;
        }

        if (!routes.fetched) {
            const unbind = emitter.on('routesLoaded', () => {
                unbind();
                callback();
            });

            return;
        }

        await callback();
    }

    /**
     * Build the selected schedule based in the selected filters.
     * @returns {null}
     */
    async function buildSelectedSchedule(options = { processNextArrival: true, updateRouteShape: false }) {
        isProcessingSchedule.value = true;
        const route = routes.current;

        if (!route?.schedule?.data) {
            return selectedSchedule.value = null;
        }

        let result = route.schedule.data
            .map((schedule) => {
                // Jump to the next iteration if schedule does not have the required data
                if (!schedule.hasOwnProperty('directionId')
                    || !schedule.hasOwnProperty('calendars')
                    || parseInt(schedule.directionId) !== parseInt(filters.directionOfTravel.selected)
                ) {
                    return null;
                }

                const dayOfTravel = filters.dayOfTravel;
                const selectedDayIndex = dayOfTravel.selectedDayIndex;
                const formattedDayOfTravel = (dayOfTravel.selectedDate)
                    ? format(dayOfTravel.selectedDate, 'yyyy/MM/dd')
                    : null;

                const availableCalendars = (dayOfTravel.selectedDate)
                    ? schedule.calendars.filter(c => {
                        if (c.negativeExceptions && c.negativeExceptions.find(ne => ne === formattedDayOfTravel)) {
                            return false;
                        }

                        if (c.positiveExceptions && c.positiveExceptions.find(ne => ne === formattedDayOfTravel)) {
                            return true;
                        }

                        return c.dows[selectedDayIndex] === true;
                    })
                    : schedule.calendars.filter(c => c.name === dayOfTravel.selected);

                // Skip this schedule if any of the calendars has the current day number as true.
                if (availableCalendars.length === 0) {
                    return null;
                }

                // Select the effective date
                const effectiveDate = (function () {
                    if (route.effectiveDates.length === 0) {
                        return undefined;
                    }

                    // Otherwise, we look for the effective date matching selected day travel,
                    // selected direction and picked date if selected.
                    return route.effectiveDates
                        .filter(effDateOpt => availableCalendars.find(c => {
                            return c.ranges.find(r => r.from === effDateOpt.fromDate && r.to === effDateOpt.toDate);
                        }))
                        .find(effDateOpt => {
                            if (! dayOfTravel?.selectedDate && dayOfTravel.selected !== effDateOpt.calendarName) {
                                return false;
                            }

                            const isEffectiveForTheDate = (dayOfTravel.selectedDate)
                                ? effDateOpt.isEffectiveFor(dayOfTravel.selectedDate)
                                : true;

                            return effDateOpt.direction === filters.directionOfTravel.selected && isEffectiveForTheDate;
                        });
                })();

                if (!effectiveDate) {
                    return null;
                }

                if (! schedule.tripCalendars.find(
                    tripCal => tripCal.name === effectiveDate.calendarName
                        && tripCal.ranges.find(r => r.from === effectiveDate.fromDate && r.to === effectiveDate.toDate),
                )) {
                    return null;
                }

                const data = cloneDeep(schedule);
                data.effectiveDate = effectiveDate;

                return data;
            })
            .filter(a => a !== null)
            .sort((a, b) => a.effectiveDate.fromDateObj - b.effectiveDate.fromDateObj)
            .shift();

        if (result) {
            const now = getNow();

            // Calculate the stops that must be displayed.
            let currentHeadsign = null;

            result.trips = result.trips.map((trip) => {
                return {
                    ...trip,
                    // Iterate through all trip's times and only keep those that
                    // has a time defined - null values mean that stop is not
                    // included in that trip.
                    stops: trip.times
                        .map((time, timeIndex) => {
                            if (!time || !time?.dep) {
                                return null;
                            }

                            return {
                                ...result.stops[timeIndex],
                                time: formatTime(transformStopTimeToDate(time.dep, getToday())),
                                label: result.stops[timeIndex].name,
                            }
                        })
                        .filter((time) => !!time),

                    alerts: (route.alert || []).filter((alert) => {
                        return Boolean((alert.entities || []).find((alertEntity) => {
                            return alertEntity.routeId.toString() === route.id.toString() && alertEntity.trips.find(
                                alertTrip => trip.tripIds.includes(alertTrip.toString())
                            );
                        }));
                    }),
                }
            });

            // Transform the stops.
            // stop = { id, code, name }
            result.stops.forEach((stop, stopIndex) => {
                stop.alerts = (route.alert || []).filter((alert) => {
                    // Check alerts from GTFS Alerts Feed
                    const matchesEntities = Boolean((alert.entities || []).find((alertEntity) => {
                        return alertEntity.routeId.toLowerCase() === route.id.toLowerCase()
                            && alertEntity.stops.find(alertStop => alertStop.stopId?.toString() === stop.id.toString());
                    }));

                    if (matchesEntities) {
                        return true;
                    }

                    //Check other alerts
                    if (! alert.routes.find(r => r.id.toLowerCase() === route.id.toLowerCase())) {
                        return false;
                    }

                    return Boolean(
                        (alert.stops || []).find(alertStop => alertStop.code?.toString() === stop.code.toString())
                    );
                });

                if (filters.dayOfTravel.selectedDate && stop.alerts.length > 0) {
                    stop.alerts = stop.alerts.filter((alert) => {
                        const selectedDate = startOfDay(filters.dayOfTravel.selectedDate);
                        const activeFrom = alert.activeFrom ? startOfDay(parseFromISO(alert.activeFrom, 'UTC')) : null;
                        const activeTo = alert.activeTo ? endOfDay(parseFromISO(alert.activeTo, 'UTC')) : null;

                        return betweenDates(selectedDate, activeFrom, activeTo);
                    });
                }

                stop.hasAlerts = stop.alerts.length > 0;

                // Get the list of times available for this stop.
                stop.times = (() => {
                    const times = [];

                    result.trips.forEach((trip, tripIndex) => {
                        if (trip.times[stopIndex]?.dep) {
                            const date = transformStopTimeToDate(trip.times[stopIndex].dep, filters.dayOfTravel.selectedDate);

                            const hasNoServiceAlert = (() => {
                                if (filters.dayOfTravel.selectedDate === null) {
                                    return false;
                                }

                                return stop.alerts.concat(trip.alerts || []).some((alert) => {
                                    if (alert.effect !== 'NO_SERVICE' && alert.effect !== 'IMPORTED_REDUCED_SERVICE') {
                                        return false;
                                    }

                                    return alert.activeRanges?.some(range => {
                                        const activeFrom = (range.from) ? parseFromISO(range.from, 'UTC') : null;
                                        const activeTo = (range.to) ? parseFromISO(range.to, 'UTC') : null;

                                        return betweenDates(date, activeFrom, activeTo);
                                    });
                                });
                            })();

                            const stopHeadsign = (() => {
                                if (result.trips[tripIndex].headsign) {
                                    return result.trips[tripIndex].headsign;
                                }

                                const searchInRefs = routes.current.refs.trips.find((trip) => {
                                    return trip.tripIds.find((tid) => stop.tripIds.includes(tid));
                                });

                                if (searchInRefs) {
                                    return searchInRefs.headsign;
                                }

                                return null;
                            })();

                            times.push({
                                date,
                                value: tripIndex.toString(),
                                label: formatTime(date),
                                nextRide: false,
                                enabled: ! hasNoServiceAlert,
                                stopIndex: stopIndex,
                                headsign: stopHeadsign
                            });
                        }
                    });

                    // Calculate the NextRide
                    if (filters.dayOfTravel.isToday) {
                        for (let t of times) {
                            if (t.enabled && isSameDay(now, t.date) && differenceInMinutes(t.date, now) >= 0) {
                                t.nextRide = true;
                                break;
                            }
                        }
                    }

                    return times;
                })();

                stop.disabled = stop.times.every((time) => !time.enabled || !time.date);
            });

            /**
             * Calculate short trip properties for ungrouped stops
             * @param {Array} stops - Array of ungrouped stops
             * @param {Array} trips - Array of trips
             */
            const calculateShortTripPropertiesForUngroupedStops = (stops, trips) => {
                stops.forEach((stop, stopIndex) => {
                    if (!stop.times) return;

                    stop.times.forEach((time) => {
                        const tripIndex = parseInt(time.value);
                        const trip = trips[tripIndex];
                        if (!trip) return;

                        // Check if the last stop has a time for this trip
                        const lastStopIndex = stops.length - 1;
                        const lastStop = stops[lastStopIndex];
                        const lastStopHasTime = trip.times[lastStopIndex]?.dep;

                        if (lastStopHasTime) {
                            time.isShortTrip = false;
                            time.isLastServedStop = false;
                            time.isStopBeforeLastServed = false;
                            return;
                        }

                        // Count stops at the end with no time for this trip
                        let nullsAtEndCount = 0;
                        for (let i = stops.length - 1; i >= 0; i--) {
                            const hasTimeForTrip = trip.times[i]?.dep;
                            if (!hasTimeForTrip) {
                                nullsAtEndCount++;
                            } else {
                                break;
                            }
                        }

                        if (nullsAtEndCount === 0) {
                            time.isShortTrip = false;
                            time.isLastServedStop = false;
                            time.isStopBeforeLastServed = false;
                            return;
                        }

                        // Find the last served stop index
                        const lastServedStopIndex = stops.length - nullsAtEndCount - 1;
                        const lastServedStop = stops[lastServedStopIndex];

                        // This is a valid short trip
                        time.isShortTrip = true;
                        time.lastServedStopName = lastServedStop.name;

                        // Determine the role of this stop
                        if (stopIndex === lastServedStopIndex) {
                            time.isLastServedStop = true;
                            time.isStopBeforeLastServed = false;
                        } else if (stopIndex < lastServedStopIndex) {
                            time.isLastServedStop = false;
                            time.isStopBeforeLastServed = true;
                        } else {
                            // This stop is after the last served stop (no time for this trip)
                            time.isLastServedStop = false;
                            time.isStopBeforeLastServed = false;
                        }
                    });
                });
            };

            /**
             * Calculate short trip properties for stopsByArea (grouped stops)
             * @param {Array} stopsByArea - Array of grouped stops
             * @param {Array} trips - Array of trips
             */
            const calculateShortTripProperties = (stopsByArea, trips) => {
                const getLastServedStopNameForTrip = (groupedStop, tripValue) => {
                    const servedStop = (groupedStop?.stops || []).find((stop) => {
                        return (stop.times || []).some((t) => t.value === tripValue && t.date);
                    });

                    return servedStop?.name || groupedStop?.name;
                };

                stopsByArea.forEach((stop, stopIndex) => {
                    if (!stop.times) return;

                    stop.times.forEach((time) => {
                        const tripIndex = parseInt(time.value);
                        const trip = trips[tripIndex];
                        if (!trip) return;

                        // Check if the last stop in stopsByArea has a time for this trip
                        const lastStopIndex = stopsByArea.length - 1;
                        const lastGroupedStop = stopsByArea[lastStopIndex];
                        const lastStopHasTime = lastGroupedStop.times?.some(t => t.value === time.value && t.date);

                        if (lastStopHasTime) {
                            time.isShortTrip = false;
                            time.isLastServedStop = false;
                            time.isStopBeforeLastServed = false;
                            return;
                        }

                        // Count groups at the end with no time for this trip
                        let nullsAtEndCount = 0;
                        for (let i = stopsByArea.length - 1; i >= 0; i--) {
                            const groupedStop = stopsByArea[i];
                            const hasTimeForTrip = groupedStop.times?.some(t => t.value === time.value && t.date);
                            if (!hasTimeForTrip) {
                                nullsAtEndCount++;
                            } else {
                                break;
                            }
                        }

                        if (nullsAtEndCount === 0) {
                            time.isShortTrip = false;
                            time.isLastServedStop = false;
                            time.isStopBeforeLastServed = false;
                            return;
                        }

                        // Find the last served stop index in stopsByArea
                        const lastServedStopIndex = stopsByArea.length - nullsAtEndCount - 1;
                        const lastServedStop = stopsByArea[lastServedStopIndex];

                        // This is a valid short trip
                        time.isShortTrip = true;
                        time.lastServedStopName = getLastServedStopNameForTrip(lastServedStop, time.value);

                        // Determine the role of this stop within stopsByArea
                        if (stopIndex === lastServedStopIndex) {
                            time.isLastServedStop = true;
                            time.isStopBeforeLastServed = false;
                        } else if (stopIndex < lastServedStopIndex) {
                            time.isLastServedStop = false;
                            time.isStopBeforeLastServed = true;
                        } else {
                            // This stop is after the last served stop (no time for this trip)
                            time.isLastServedStop = false;
                            time.isStopBeforeLastServed = false;
                        }
                    });
                });
            };

            // Calculate the "Runs on" text.
            result.runsOn = computed(() => result.tripCalendars.map((c, index) => {
                const ranges = c.ranges.map((r) => {
                    const runsOn = getRunsOn();
                    const dateFrom = format(parse(r.from, 'yyyy/MM/dd', getToday()), 'MMM do');
                    const dateTo = format(parse(r.to, 'yyyy/MM/dd', getToday()), 'MMM do');

                    return `<span>${runsOn} from <strong class="font-bold">${dateFrom}</strong> to <strong class="font-bold">${dateTo}</strong></span>`;
                });

                const exceptions = (c.negativeExceptions || []).map((e, index) => {
                    const date = format(parse(e, 'yyyy/MM/dd', getToday()), 'MMM do');

                    const prefix = (index === 0)
                        ? 'except '
                        : '';

                    return `${prefix}<strong>${date}</strong>`
                });

                const label = ranges.length ? 'Runs ' : ''

                return {
                    index,
                    name: c.name,
                    description: (index === 0 ? label : `<sup>${index}</sup>`) + ranges.concat(exceptions).join(', '),
                }
            }));

            // Calculate selected times for stops.
            let tripId = parseInt(filters.timeOfTravel.selected);

            if (filters.dayOfTravel.isToday) {
                if (filters.timeOfTravel.selected === '-1') {
                    setTimeOfTravelValue(getNextArrivalFromStops(result.stops)?.value);
                    tripId = parseInt(filters.timeOfTravel.selected);
                }
            }

            if (!result.trips[tripId]) {
                setTimeOfTravelValue(0);
                tripId = parseInt(filters.timeOfTravel.selected);
            }

            result.stops.forEach((stop, stopIndex) => {
                // Calculate the selected time for the stop
                stop.selectedTime = result.trips[tripId].times[stopIndex]?.dep
                    ? stop.times.find((time) => time.value === tripId.toString())
                    : null;

                if (stop.selectedTime) {
                    // Get the stop's headsign, if applicable.
                    const stopHeadsign = (() => {
                        if (result.trips[tripId].headsign) {
                            return result.trips[tripId].headsign;
                        }

                        const searchInRefs = routes.current.refs.trips.find((trip) => {
                            return trip.tripIds.find((tid) => stop.tripIds.includes(tid));
                        });

                        if (searchInRefs) {
                            return searchInRefs.headsign;
                        }

                        return null;
                    })();

                    if (currentHeadsign !== stopHeadsign) {
                        stop.headsign = currentHeadsign = stopHeadsign;
                    }
                }
            });

            // Group stops by area ids
            const areas = result.areas.reduce((acc, area) => {
                return {
                    ...acc,
                    [area.id]: area.name,
                }
            }, {});

            let groupedStopsByArea = result.stops
                .reduce((acc, stop, stopIndex) => {
                    const groupKeys = (! stop.areaIds || stop.areaIds.length === 0)
                        ? [stop.code]
                        : stop.areaIds;

                    groupKeys.forEach((groupKey) => {
                        if (!acc.hasOwnProperty(groupKey)) {
                            acc[groupKey] = {};
                        }

                        acc[groupKey][stopIndex] = {
                            id: groupKey,
                            stopIndex: stopIndex,
                            stop: stop,
                            name: areas[groupKey],
                        };
                    });

                    return acc;
                }, {});

            // IMPORTANT: If this logic is updated, make sure to update grouped stops logic in app/Domain/PrintableSchedule/Traits/MakesScheduleCalendarEntity.php
            // TODO: Build it into an API to havew a single place of truth for this logic and avoid potential
            // inconsistencies between frontend and backend grouped stops.
            groupedStopsByArea = Object.keys(groupedStopsByArea).reduce((acc, groupKey) => {
                const groupReduced = Object.keys(groupedStopsByArea[groupKey]).reduce((groupAcc, objIndex) => {
                    const item = groupedStopsByArea[groupKey][objIndex];

                    // When this stop order is sequential to the previous stop order, we merge them as one single stop.
                    // It will be done for multiple sequential stops.
                    const order = (groupAcc.legsMap.hasOwnProperty(item.stopIndex - 1))
                        ? groupAcc.legsMap[item.stopIndex - 1]
                        : item.stopIndex;

                    // Map the current stop index to the leg order it belongs to, so when we find a sequential stop index
                    // we can easily get the leg order and merge them.
                    groupAcc.legsMap[item.stopIndex] = order;

                    // Get the existing data for this order if exists, otherwise initialise it with current stop data.
                    const data = groupAcc.legs[order] || {
                        areaId: item.id,
                        name: item.name,
                        stopIndex: item.stopIndex,
                    };

                    groupAcc.legs[order] = {
                        ...data,
                        alerts: [
                            ...data.alerts || [],
                            ...item.stop.alerts || [],
                        ],
                        hasAlerts: data.hasAlerts || item.stop.hasAlerts,
                        disabled: data.disabled || item.stop.disabled,
                        stops: [
                            ...data.stops || [],
                            item.stop,
                        ]
                    };

                    return groupAcc;
                }, { legsMap: {}, legs: {} });

                const groupAreaLegs = Object.values(groupReduced.legs).map((item) => {
                    const times = item.stops.reduce((acc, stop) => {
                        stop.times.forEach((time) => {
                            if (! acc[time.value]?.date) {
                                // Initialize short trip properties (will be calculated later for stopsByArea)
                                acc[time.value] = {
                                    ...time,
                                    isShortTrip: false,
                                    isLastServedStop: false,
                                    isStopBeforeLastServed: false,
                                    lastServedStopName: undefined,
                                };
                            }
                        });

                        return acc;
                    }, {});

                    const selectedTimeStop = item.stops.find((stop) => stop.selectedTime !== null);
                    const nextRide = Object.values(times).find((time) => time.nextRide);

                    // Get the sorted times array
                    const sortedTimes = sortBy(Object.values(times), (t) => parseInt(t.value) || -1)
                        .map((time) => {
                            time.nextRide = (nextRide?.value && time.value === nextRide.value);
                            return time;
                        });

                    // Find selectedTime from the times array to ensure it has the calculated short trip properties
                    const selectedTime = selectedTimeStop?.selectedTime
                        ? sortedTimes.find((time) => time.value === selectedTimeStop.selectedTime.value)
                        : null;

                    return {
                        ...item,
                        // Use the area name as the stop name because multiple stops sharing the same area needs
                        // to display the same head sign [...]
                        name: item.name,
                        // [...] but for id and code let's use the stop properties as such information are not
                        // provided by area, and we also want to show the stop info anyway as an area can have
                        // multiple stops (usually 2) in same location.
                        id: selectedTimeStop?.id || item.stops[0]?.id,
                        code: selectedTimeStop?.code || item.stops[0]?.code,
                        selectedTime: selectedTime,
                        times: sortedTimes,
                    }
                });

                return acc.concat(groupAreaLegs);
            }, []);

            // Assign stopsByArea and calculate short trip properties for grouped stops
            result.stopsByArea = sortBy(groupedStopsByArea, 'stopIndex');

            // Conditionally calculate short trip properties based on feature flag
            const groupStopsByAreaEnabled = page.props.global.feature_flags?.maps_and_schedules_group_stops_by_area;

            if (groupStopsByAreaEnabled) {
                // New way: Calculate on grouped stops (stopsByArea)
                calculateShortTripProperties(result.stopsByArea, result.trips);
            } else {
                // Old way: Calculate on ungrouped stops (result.stops)
                calculateShortTripPropertiesForUngroupedStops(result.stops, result.trips);
            }

            // Load the route shape
            result.shape = selectedSchedule.value?.shape;

            if (route.id && options.updateRouteShape) {
                const routeShape = await api.getRouteShapes(
                    route.id,
                    result.effectiveDate.fromDate ? result.effectiveDate.fromDate.replaceAll('/', '-') : null,
                    result.effectiveDate.toDate ? result.effectiveDate.toDate.replaceAll('/', '-') : null
                );

                const bounds = routeShape.data.stops.reduce(function (bounds, stop) {
                    stop = {
                        lat: stop.location.lat,
                        lng: stop.location.lng,
                    }

                    if (!bounds) {
                        return {
                            min: {lat: stop.lat, lng: stop.lng},
                            max: {lat: stop.lat, lng: stop.lng},
                            center: {lat: stop.lat, lng: stop.lng}
                        }
                    }

                    let routeBounds = {
                        min: {
                            lat: Math.min(stop.lat, bounds.min.lat),
                            lng: Math.min(stop.lng, bounds.min.lng)
                        },
                        max: {
                            lat: Math.max(stop.lat, bounds.max.lat),
                            lng: Math.max(stop.lng, bounds.max.lng)
                        }
                    }

                    routeBounds.center = {
                        lat: (routeBounds.min.lat + routeBounds.max.lat) / 2,
                        lng: (routeBounds.min.lng + routeBounds.max.lng) / 2
                    }

                    return routeBounds
                }, false)

                if (routeShape.data.routes.length > 0) {
                    result.shape = {
                        lines: routeShape.data.routes[0].multiGeometry.lineStrings,
                        stops: result.stops.map((stop) => {
                            return routeShape.data.stops.find((shapeStop) => shapeStop.id === stop.id && shapeStop.code === stop.code);
                        }).filter((stop) => stop !== undefined),
                        allStops: routeShape.data.stops,
                        bounds: bounds,
                    }
                } else {
                    result.shape = {
                        line: [],
                        stops: [],
                        allStops: [],
                        bounds: [],
                    }
                }
            }
        }

        isProcessingSchedule.value = false;
        selectedSchedule.value = result || {
            empty: true,
        };

        return selectedSchedule.value;
    }

    function setSelectedStops(stops) {
        selectedStops.value = stops;
    }

    function isStopSelected(stop) {
        const index = findIndex(selectedStops.value, (s) => s.code === stop.code);

        return index > -1;
    }

    function startLoading() {
        isLoading.value = true;
    }

    function setupWatchers() {
        if (unwatchHandlers.length) {
            unwatchHandlers.forEach(unwatch => unwatch())
            unwatchHandlers = []
        }

        unwatchHandlers.push(
            watch(() => [
                filters.dayOfTravel.selected,
                filters.directionOfTravel.selected,
                filters.timeOfTravel.selected,
                routes.current,
            ], (newValues, oldValues) => {
                buildSelectedSchedule({
                    updateRouteShape: true,
                    processNextArrival: false
                });
            })
        );
    }

    async function initStore() {
        setupWatchers()

        if (!api) {
            api = inject('api');
        }

        if (!isLoading.value && !dataFetched.value) {
            await loadDataFromApi();
        }
    }

    return {
        // flags
        isLoading,
        isProcessingSchedule,
        dataFetched,

        // data
        routes,
        routeGroups,
        selectedSchedule,
        selectedStops,
        alerts,
        filters,
        routeSearch,

        // actions
        startLoading,
        setCurrentRoute,
        setSelectedRoutes,
        setDayOfTravelDate,
        setDayOfTravelValue,
        setTimeOfTravelValue,
        getNextArrivalFromStops,
        setDirectionOfTravelValue,
        isStopSelected,
        setSelectedStops,
        setGroupScrolls,
        setRouteScroll,
        initStore,

        // etc.
        configureRouteColorData,
    }
})
